<template>
  <div
    class="content-container"
  >
    <div class="action-buttons-header">
      <button
        class="btn btn-outline-primary"
        @click="showSelfResponseModal = true"
      >
        {{ $localize('AddNewUploadFolder') }}
      </button>
    </div>
    <h2 class="page-title-heading">
      <span>{{ $localize('UploadFolders') }}</span>
      <span
        v-if="!loading || !!paginationOptions.total"
        class="font-size-smaller"
      > ({{ paginationOptions.total }})</span>
    </h2>
    <p class="lead">
      {{ $localize('ViewAndManageUploadFolders') }}
    </p>
    <div class="requests">
      <!-- Left area for the filter panel -->
      <div class="requests__filter-panel">
        <div class="filter-panel__heading-container">
          <div
            class="filter-panel__heading"
            data-test="filter-panel-heading"
          >
            {{ $localize('RequestFilterTitle') }}
          </div>
          <a
            data-test="reset-filter-link"
            href="#"
            @click.prevent.stop="resetFilterCriteriaToDefault"
          >{{ $localize('ResetAll') }}</a>
        </div>
        <vue-filter-panel
          v-if="initialFilter"
          ref="filter"
          :initial-filter="initialFilter"
          :refine-by-tooltip="$localize('SelfResponseRefineByTooltip')"
          :local-storage-key="LocalStorageKeyType.UploadFoldersFilterCriteria"
          @filter-change-handler="filterChangeHandler"
        />
      </div>
      <!-- Right area for the request list -->
      <div
        class="requests__main-content"
      >
        <UploadFoldersList
          v-model:selected-page="paginationOptions.currentPage"
          v-model:page-size="paginationOptions.pageSize"
          :upload-folders="folders"
          :last-accessed-response-id="previousRoute.id"
          :loading-error-message="$localize('FailedToRetrieveUploadFolders')"
          :loading="loading || loadingPage"
          :display-loading-error="displayLoadingError"
          :pagination-total-items="paginationOptions.total"
          :sorting-options="sortingOptions.options"
          :sorting-default-order="sortingOptions.sortOrder"
          :sorting-default-value="sortingOptions.sortField"
          @on-sort="sortingOptions.onSort($event)"
          @page-changed="paginationOptions.onPageChanged()"
          @page-size-changed="paginationOptions.onPageSizeChanged()"
        />
      </div>
    </div>

    <SelfResponseModal
      v-model="showSelfResponseModal"
    />
  </div>
</template>
<script lang="ts" setup>
  import { nextTick, reactive, ref } from 'vue';
  import { ApplicationError } from '@/Services/Clients/applicationError';
  import { InitialFilterCriteriaDto } from '@/VueComponents/Filter/Dtos/initialFilterCriteriaDto';
  import UploadFoldersListModel from '@/VueComponents/UploadFolders/UploadFoldersList/Models/uploadFoldersListModel';
  import logger from '@/Utils/logger';
  import { LocalStorageKeyType } from '@/Types/Enums/localStorageKeyType';
  import UploadFoldersList from '@/VueComponents/UploadFolders/UploadFoldersList/UploadFoldersList.vue';
  import { SelfResponseSummary } from '@/Models/selfResponseSummary';
  import SelfResponseModal from '@/VueComponents/Modals/CreateSelfResponse/CreateSelfResponse.vue';
  import VueFilterPanel from '@/VueComponents/Filter/VueFilterPanel.vue';
  import moment from 'moment';
  import { load } from '@/VueComponents/UploadFolders/utils/uploadFolderSummaryLoader';
  import resourceHelper from '@/Utils/resourceHelper';
  import SortOptionsManager from '@/VueComponents/SharedComponents/SortDropdown/utils/sortOptionsManager';
  import PaginationOptionsManager
    from '@/VueComponents/PaginatedTable/TablePageNavigation/utils/paginationOptionsManager';
  import { useContextDataStore } from '@/VueCore/stores/contextDataStore';
  import usePageLoading from '@/VueCore/utils/usePageLoading';
  import SelfResponseSummaryService from '@/Services/selfResponseSummaryService';
  import { PreviousRoute } from '@/VueCore/routing/previousRoute';

  const contextData = useContextDataStore();
  const props = defineProps<{
    previousRoute: PreviousRoute
  }>();

  const usePreviousViewSettings = props.previousRoute.page === 'UploadFolderDetails';
  const initialFilter = ref<InitialFilterCriteriaDto | null>(null);

  const { loadingPage } = usePageLoading(async () => {
    initialFilter.value = await SelfResponseSummaryService.getInitialFilter();
    await nextTick();
    await updateResults();
  }, error => console.error('Failed to fetch initial filter:', error));

  const loading = ref(false);
  const displayLoadingError = ref(false);
  let folders: UploadFoldersListModel[] = reactive([]);
  const showSelfResponseModal = ref(false);

  const sortingOptions = reactive(new SortOptionsManager(
    [{ field: 'CreatedTimestamp', label: resourceHelper.getString('DateCreated') }],
    LocalStorageKeyType.UploadFoldersSorting,
    () => {
      paginationOptions.currentPage = 1;
      updateResults();
    },
    'Desc'
  ));

  const paginationOptions = reactive(new PaginationOptionsManager(
    contextData.portalSettings.paginationPageSizeOptions,
    LocalStorageKeyType.UploadFoldersPage,
    LocalStorageKeyType.PaginationUploadFoldersPageSize,
    () => {
      updateResults();
    },
    () => {
      if (paginationOptions.pageSize > paginationOptions.total && folders.length === paginationOptions.total) {
        return;
      }

      updateResults();
    },
    { page: usePreviousViewSettings, pageSize: true }
  ));

  const filter = ref(null);

  const updateResults = () => {

    const filterCriteria = filter.value.getSelectedFilterCriteria();

    loading.value = true;

    return load(filterCriteria,
                sortingOptions.sortField,
                sortingOptions.sortOrder,
                paginationOptions.currentPage,
                paginationOptions.pageSize,
                pageResult => {

                  loading.value = false;
                  folders = pageResult.results.map(mapToUploadFolderListModel);

                  filter.value?.updateFilterCriteria(pageResult.filterCriteria);
                  paginationOptions.total = pageResult.total;

                  // reload the last available page if current page does not contain any elements
                  if (paginationOptions.total && !pageResult.results.length) {
                    paginationOptions.currentPage = Math.ceil(paginationOptions.total / paginationOptions.pageSize);

                    updateResults();
                  }
                },
                (error: ApplicationError) => {
                  loading.value = false;
                  displayLoadingError.value = true;

                  if (!error.isLogged) {
                    logger.error('UnexpectedErrorWhileRetrievingUploadFolders', null, error);
                  }
                }
    );
  };

  const filterChangeHandler = () => {
    paginationOptions.currentPage = 1;

    updateResults();
  };

  const resetFilterCriteriaToDefault = () => {
    filter.value.applyDefaultFilterCriteria();
    filterChangeHandler();
  };

  function mapToUploadFolderListModel(response: SelfResponseSummary): UploadFoldersListModel {
    return {
      assignedTo: response.assignedPersonaDisplayName || response.assignedGroupDisplayName,
      createBy: response.createdByPersonaDisplayName,
      createdOn: moment(response.createdDateTime).format('LL'),
      id: response.id,
      internalReferenceNumber: response.internalReferenceNumber,
      referenceNumber: response.referenceNumber,
      statusName: response.selfResponseStatusForDisplay,
      discussionMessagesCount: response.discussionMessagesCount,
      priorityName: response.selfResponsePriorityForDisplay,
      rmsId: response.rmsId,
      cadIds: response.cadIds
    };
  }
</script>
<style scoped>
</style>